import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import get from 'lodash.get';
import search from 'javascript-array-search';

// Selectors
import { makeSelectHarvestSchemaValue } from '../../../../../../services/schemas/selectors';
import { selectActiveEntitiesArray } from '../../../../../../services/entities/selectors';
import { selectCurrentEntityId } from '../../../../../../services/currentEntityId/selectors';
import { selectVarieties } from '../../../../../../services/varieties/selectors';
import { selectQuantityForecasts } from '../../../../../../services/quantityForecasts/selectors';

// Workspaces
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from '../../../../../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../../../../../utils/custom-orderBy';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectRelevantData = createDeepEqualSelector(
    [
        selectActiveEntitiesArray,
        makeSelectHarvestSchemaValue('entityTypeId'),
        selectCurrentEntityId
    ],
    (activeEntitiesArray, harvestSchemaEntityTypeId, currentEntityId) =>
        activeEntitiesArray.filter(
            item =>
                item.entityTypeId === harvestSchemaEntityTypeId &&
                (currentEntityId
                    ? item.path && item.path.includes(`/${currentEntityId}`)
                    : true)
        )
);

const selectDenormalizedData = createDeepEqualSelector(
    [selectRelevantData, selectVarieties, selectQuantityForecasts],
    (relevantDataState, varietiesState, quantityForecastsState) =>
        relevantDataState.map(item => {
            const { defaultVarietyId, id } = item;
            const variety = get(varietiesState, defaultVarietyId, {});
            const quantityForecast = get(quantityForecastsState, id, {});
            const { value, size } = quantityForecast;

            return {
                ...item,
                variety,
                quantityForecast: {
                    value: !value && value !== 0 ? -1 : value,
                    size: !size && size !== 0 ? -1 : size
                }
            };
        })
);

const selectFilteredData = createDeepEqualSelector(
    [
        selectDenormalizedData,
        makeSelectQueryStringValue('search'),
        makeSelectQueryStringNumberValue('varieties'),
        makeSelectQueryStringNumberValue('entities')
    ],
    (dataState, searchState, varietiesState, entitiesState) => {
        const searchedData = search(dataState, searchState, [
            'name',
            'orchardRpin',
            'variety.name'
        ]);
        return searchedData.filter(value => {
            const { defaultVarietyId, path } = value;

            if (varietiesState && varietiesState !== defaultVarietyId) {
                return false;
            }
            if (entitiesState && !`${path}/`.includes(entitiesState)) {
                return false;
            }
            return value;
        });
    }
);

const selectDataLength = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => filteredDataState.length
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, makeSelectQueryStringValue('sort')],
    (filteredDataState, sortState = 'name_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';
        return orderBy(
            filteredDataState,
            [propertyName, 'name'],
            [order, 'asc']
        );
    }
);

const selectPaginatedData = createDeepEqualSelector(
    [
        selectOrderedData,
        makeSelectQueryStringNumberValue('page'),
        makeSelectQueryStringNumberValue('count')
    ],
    (orderedDataState, pageState = 1, countState = 25) => {
        const begin = (pageState - 1) * countState;
        const end = pageState * countState;
        return orderedDataState.slice(begin, end);
    }
);

const selectEntityIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState =>
        paginatedDataState && paginatedDataState.map(value => value.id)
);

export {
    selectDataLength,
    selectFilteredData,
    selectOrderedData,
    selectEntityIds
};
